import { action, computed, makeObservable } from 'mobx';

import { ValueModel } from '@/models/ValueModel';

type Options<T> = {
  key: string;
  fallback: T;
  /** Сохранённое могло остаться от старой версии или быть испорчено */
  isValid: (saved: unknown) => saved is T;
};

const read = <T>({ key, fallback, isValid }: Options<T>): T => {
  try {
    const saved: unknown = JSON.parse(window.localStorage.getItem(key) ?? 'null');

    return isValid(saved) ? saved : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Значение, которое помнит этот браузер (localStorage). Хранилище бывает недоступно (приватный режим)
 * или содержит старый формат — тогда значение по умолчанию, а новое просто не запоминается
 */
export class StoredValueModel<T> {
  private readonly _key: string;
  private readonly _value: ValueModel<T>;

  constructor({ key, fallback, isValid }: Options<T>) {
    this._key = key;
    this._value = new ValueModel(read({ key, fallback, isValid }));

    makeObservable(this, {
      value: computed,
      change: action.bound,
    });
  }

  get value() {
    return this._value.value;
  }

  change(value: T) {
    this._value.change(value);

    try {
      window.localStorage.setItem(this._key, JSON.stringify(value));
    } catch {
      // Не запомнили — значение доживёт до перезагрузки страницы
    }
  }
}

export const isBoolean = (saved: unknown): saved is boolean => typeof saved === 'boolean';
